import React, { useState } from 'react'
import Fuse from 'fuse.js/dist/fuse.basic'
import {
  SelectEntitiesModalDataContext,
  SelectEntitiesModalQueryContext
} from './context'
import {
  MatchedSearchableItem,
  SearchableItem
} from './SelectEntitiesForm.types'
import { useMapMutableProps } from '../../../app/hooks/useMapMutableProps'
import {
  findSearchTermEntryIndex,
  mapEntitiesToSearchableItem,
  plainSearch
} from './utils'
import { useDebouncedState } from 'front-end/src/app/hooks/useDebouncedState'

export const MIN_FUZZY_SEARCH_MATCH_LEN = 2
export const MAIN_SEARCHABLE_ITEM_TEXT_PROPERTY_KEY = 'text'
const naiveMatchSearchableItems = (
  items: SearchableItem[]
): MatchedSearchableItem[] => {
  return items.map(findSearchTermEntryIndex(''))
}

export const useFilterSuggestions = (
  initialOptions: SearchableItem[],
  search: string,
  fields: string[] = [MAIN_SEARCHABLE_ITEM_TEXT_PROPERTY_KEY]
): MatchedSearchableItem[] => {
  const nextFilterImmediate = React.useRef<NodeJS.Timeout | null>(null)
  const [filtered, setFiltered] = React.useState(() =>
    naiveMatchSearchableItems(initialOptions)
  )
  const fuzzySearchEngineRef = React.useRef<Fuse | null>(null)

  React.useEffect(() => {
    nextFilterImmediate.current = setTimeout(() => {
      if (!search) {
        return setFiltered(naiveMatchSearchableItems(initialOptions))
      }
      if (
        !fuzzySearchEngineRef.current ||
        search.length < MIN_FUZZY_SEARCH_MATCH_LEN
      ) {
        return setFiltered(
          plainSearch(initialOptions, search.trim(), (v) =>
            v.text.indexOf(search)
          )
        )
      }
      const matched = fuzzySearchEngineRef.current.search(search.trim())
      setFiltered(matched)
    })

    return () => {
      if (nextFilterImmediate.current) {
        clearTimeout(nextFilterImmediate.current)
      }
    }
  }, [search])

  React.useEffect(() => {
    const timeoutSetupEngine = setTimeout(() => {
      fuzzySearchEngineRef.current = new Fuse(
        [...initialOptions], // otherwise Fuse.js will clear all array on destroy
        {
          keys: fields,
          shouldSort: true,
          includeMatches: true,
          minMatchCharLength: MIN_FUZZY_SEARCH_MATCH_LEN,
          includeScore: true,
          ignoreLocation: true,
          threshold: 0.3,
          ignoreFieldNorm: true
        }
      )
    }, 1)
    return () => {
      clearTimeout(timeoutSetupEngine)
      if (nextFilterImmediate.current) {
        clearTimeout(nextFilterImmediate.current)
      }
      if (fuzzySearchEngineRef.current) {
        fuzzySearchEngineRef.current.remove(() => true) // cleanup
        fuzzySearchEngineRef.current = null
      }
    }
  }, [initialOptions])

  return filtered
}

type FuzzyResourcesFilterProps = {
  fields?: string[]
}

export const FuzzyResourcesFilter: React.FC<FuzzyResourcesFilterProps> = ({
  children,
  fields
}) => {
  const [query, setQuery] = useState('')
  const { entities } = React.useContext(SelectEntitiesModalDataContext)
  const [items] = useMapMutableProps(entities, mapEntitiesToSearchableItem)
  const debouncedQuery = useDebouncedState(query, 100)
  const filteredItems = useFilterSuggestions(items, debouncedQuery, fields)

  return (
    <SelectEntitiesModalQueryContext.Provider
      value={{
        items: filteredItems,
        query,
        setQuery
      }}
    >
      {children}
    </SelectEntitiesModalQueryContext.Provider>
  )
}
